import type {
  CostResponse,
  CostSummaryResponse,
  Health,
  HistoryResponse,
  MeterView,
  MetricMeta,
  PricingInfo,
} from "./types";

/**
 * Базовый адрес API.
 *
 * В разработке фронтенд живёт на :5173, бэкенд на :8080 — адрес задаётся
 * через VITE_API_BASE. В продакшене бэкенд раздаёт собранную статику сам,
 * поэтому базовый адрес пустой и запросы идут на тот же хост.
 */
export const apiBase: string = import.meta.env.VITE_API_BASE ?? "";

/** Адрес WebSocket выводится из apiBase, чтобы не задавать его отдельно. */
export function wsURL(): string {
  const base = apiBase || window.location.origin;
  const u = new URL(base, window.location.href);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws";
  u.search = "";
  return u.toString();
}

/**
 * Ошибка «нужен вход».
 *
 * Отдельный тип, а не текст сообщения: приложению нужно отличить «сессия
 * кончилась» от любой другой ошибки сети и показать экран входа, а не баннер
 * «нет связи с сервером».
 */
export class UnauthorizedError extends Error {
  constructor() {
    super("нужен вход");
    this.name = "UnauthorizedError";
  }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, { signal, credentials: "same-origin" });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) detail = body.error;
    } catch {
      // тело не JSON — оставляем статус
    }
    throw new Error(`${path}: ${detail}`);
  }
  return res.json() as Promise<T>;
}

/** Состояние входа: нужен ли пароль и введён ли он. */
export interface Session {
  authRequired: boolean;
  authenticated: boolean;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error(`${path}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export const api = {
  /** Нужен ли вход и выполнен ли он. Ручка открыта всегда. */
  session: (signal?: AbortSignal) => get<Session>("/api/session", signal),

  /** Вход по паролю; сессия остаётся в HttpOnly-куке. */
  login: (password: string) => post<Session>("/api/login", { password }),

  logout: () => post<Session>("/api/logout"),

  health: (signal?: AbortSignal) => get<Health>("/api/health", signal),
  meters: (signal?: AbortSignal) => get<MeterView[]>("/api/meters", signal),
  metrics: (signal?: AbortSignal) => get<MetricMeta[]>("/api/metrics", signal),

  /**
   * История замеров.
   * range — относительное окно ("15m", "1h", "24h").
   * bucket не передаём: сервер сам подбирает прореживание под ~500 точек.
   */
  history: (meterId: number, range: string, metrics: string[], signal?: AbortSignal) => {
    const q = new URLSearchParams({ range });
    if (metrics.length) q.set("metrics", metrics.join(","));
    return get<HistoryResponse>(`/api/meters/${meterId}/history?${q}`, signal);
  },

  /** Настройки подсчёта стоимости и текущая биржевая цена. */
  pricing: (signal?: AbortSignal) => get<PricingInfo>("/api/pricing", signal),

  /** Стоимость потреблённой энергии за период (range: "15m", "1h", "24h"). */
  cost: (meterId: number, range: string, signal?: AbortSignal) =>
    get<CostResponse>(`/api/meters/${meterId}/cost?range=${encodeURIComponent(range)}`, signal),

  /** Сводка стоимости за календарные сутки и месяц. */
  costSummary: (meterId: number, signal?: AbortSignal) =>
    get<CostSummaryResponse>(`/api/meters/${meterId}/cost/summary`, signal),
};
